import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  countVisites,
  countVisitesByDate,
  deleteVisitesByDate,
} from "../../../../../services/visitesServices";

import {
  getDefaultDateRange,
  getVisitsStats,
  isDemoMode,
  normalizeVisitRows,
} from "../utils/visitsManagerUtils";

export default function useVisitsManager() {
  const [rows, setRows] = useState([]);
  const [totalVisites, setTotalVisites] = useState(0);

  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchVisits = useCallback(async () => {
    try {
      setLoading(true);

      const [rowsResponse, totalResponse] = await Promise.all([
        countVisitesByDate(),
        countVisites(),
      ]);

      const normalizedRows = normalizeVisitRows(rowsResponse);

      setRows(normalizedRows);
      setTotalVisites(Number(totalResponse?.count || 0));

      const range = getDefaultDateRange(normalizedRows);
      setDateDebut((current) => current || range.dateDebut);
      setDateFin((current) => current || range.dateFin);
    } catch (error) {
      console.error("Erreur lors du chargement des visites :", error);
      toast.error("Erreur lors du chargement des visites.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const stats = useMemo(() => {
    return getVisitsStats(rows, totalVisites);
  }, [rows, totalVisites]);

  const handleDeleteVisits = async () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    if (!dateDebut || !dateFin) {
      toast.error("Veuillez sélectionner une date de début et une date de fin.");
      return;
    }

    if (new Date(dateDebut) > new Date(dateFin)) {
      toast.error("La date de début doit être inférieure à la date de fin.");
      return;
    }

    const confirmed = window.confirm(
      `Supprimer les visites du ${dateDebut} au ${dateFin} ?`
    );

    if (!confirmed) return;

    try {
      setDeleting(true);

      const response = await deleteVisitesByDate(dateDebut, dateFin);

      toast.success(response?.message || "Visites supprimées avec succès.");
      await fetchVisits();
    } catch (error) {
      console.error("Erreur lors de la suppression des visites :", error);
      toast.error("Erreur lors de la suppression des visites.");
    } finally {
      setDeleting(false);
    }
  };

  return {
    rows,
    stats,
    loading,
    deleting,
    demoMode,
    dateDebut,
    dateFin,

    setDateDebut,
    setDateFin,
    refreshData: fetchVisits,
    handleDeleteVisits,
  };
}